import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import PWAInstallBanner from '../components/PWAInstallBanner';
import FeaturedVendors from '../components/FeaturedVendors';
import HouseBrandRail from '../components/HouseBrandRail';
import TestimonialsSection from '../components/TestimonialsSection';
import InstallAppSection from '../components/InstallAppSection';
import FeaturedStorySection from '../components/FeaturedStorySection';
import FoundingSpotsCounter from '../components/FoundingSpotsCounter';
import { Button } from '../components/ui/button';
import {
  ArrowRight, ShieldCheck, Globe, Lock, HandHeart, CheckCircle2,
  Sparkles, ShoppingBag, Store as StoreIcon, Church, Users, ShoppingCart
} from 'lucide-react';
import { API } from '../App';

const HERO_FLYER_SLIDES = [
  {
    src: '/hero-community.jpg',
    alt: 'Diverse Christian community — buyers, sellers, and ministries united by faith',
    label: null,
    bgClass: 'bg-[hsl(var(--navy-dark))]',
  },
  {
    src: '/hero-couple.jpg',
    alt: 'A Christian couple shopping on the Christ Kingdom Global Marketplace app',
    label: null,
    bgClass: 'bg-[hsl(var(--navy-dark))]',
  },
];

const CORE_VALUES = ['Integrity', 'Excellence', 'Service', 'Faith', 'Love', 'Impact'];

const AUDIENCE_COLUMNS = [
  {
    icon: ShoppingBag,
    title: 'For Buyers',
    bullets: [
      'Discover amazing Christian products & services',
      'Shop from trusted businesses worldwide',
      'Secure payments & fast transactions',
      'Support Kingdom-minded entrepreneurs',
    ],
  },
  {
    icon: StoreIcon,
    title: 'For Sellers',
    bullets: [
      'Grow your business with purpose',
      'Reach a global Christian audience',
      'Easy store setup & product management',
      'Secure payouts & dedicated support',
    ],
  },
  {
    icon: Church,
    title: 'For Ministries',
    bullets: [
      'Promote your ministry & events',
      'Receive donations & support',
      'Share resources & build community',
      'Expand your Kingdom impact',
    ],
  },
  {
    icon: Users,
    title: 'For Everyone',
    bullets: [
      'A place for all Christians',
      'United by faith, connected in love',
      'Building the Kingdom one transaction at a time',
    ],
  },
];

const TRUST_BADGES = [
  { icon: ShieldCheck, label: 'Trusted Community' },
  { icon: Globe, label: 'Global Reach' },
  { icon: Lock, label: 'Secure Payments' },
  { icon: HandHeart, label: 'Kingdom Impact' },
];

export default function LandingPage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [houseBrandProducts, setHouseBrandProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slideIdx, setSlideIdx] = useState(0);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const [featured, apparel] = await Promise.all([
          axios.get(`${API}/products?limit=8`),
          axios.get(`${API}/products?category=apparel&limit=20`),
        ]);
        setFeaturedProducts(featured.data.products || []);
        setHouseBrandProducts(apparel.data.products || []);
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Combine flyer extracts + Christ Kingdom Apparel (sweatshirt / youth tee / hoodie / etc)
  const heroSlides = React.useMemo(() => {
    const apparelSlides = (houseBrandProducts || [])
      .filter((p) => (p.vendor_name || '').toLowerCase().includes('christ kingdom'))
      .map((p) => {
        const url = (p.images && p.images[0]) || p.primary_image;
        return url
          ? {
              src: url,
              alt: p.name
                ? `${p.name} — Christ Kingdom Apparel`
                : 'Christ Kingdom Apparel',
              label: p.name,
              bgClass: 'bg-white',
            }
          : null;
      })
      .filter(Boolean);
    return [...HERO_FLYER_SLIDES, ...apparelSlides];
  }, [houseBrandProducts]);

  // Reset index if slides shrink
  useEffect(() => {
    if (slideIdx >= heroSlides.length) setSlideIdx(0);
  }, [heroSlides.length, slideIdx]);

  // Auto-rotate hero slides every 4.5s
  useEffect(() => {
    if (heroSlides.length <= 1) return;
    const id = setInterval(() => {
      setSlideIdx((i) => (i + 1) % heroSlides.length);
    }, 4500);
    return () => clearInterval(id);
  }, [heroSlides.length]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <PWAInstallBanner />
      <Header />

      {/* ─────────── HERO — navy + gold, matching flyer ─────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[hsl(var(--navy-dark))] via-[hsl(var(--navy))] to-[hsl(var(--navy))]" data-testid="hero-section">
        {/* Decorative gold arc — top right */}
        <div className="absolute top-0 right-0 w-[60%] h-full opacity-10 pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full border-[40px] border-[hsl(var(--gold))]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3 text-white">
            <div className="text-[hsl(var(--gold))] font-heading text-sm md:text-base tracking-[0.35em] font-medium mb-4">
              FAITH · COMMUNITY · COMMERCE
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold leading-[1.05] mb-6" data-testid="hero-title">
              A Global Marketplace for Christians,
              <span className="text-[hsl(var(--gold))]"> By Christians.</span>
            </h1>
            <p className="text-lg md:text-xl text-white/85 leading-relaxed mb-8 max-w-2xl">
              Buy and sell quality products and services worldwide while building the
              Kingdom together.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/products">
                <Button size="lg" className="bg-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/90 text-[hsl(var(--navy-dark))] font-semibold px-8 rounded-full" data-testid="hero-shop-button">
                  Shop Now <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/register">
                <Button size="lg" variant="outline" className="border-white/40 bg-transparent text-white hover:bg-white/10 px-8 rounded-full" data-testid="hero-sell-button">
                  Start Selling
                </Button>
              </Link>
            </div>
          </div>

          {/* Right — auto-rotating community slideshow (flyer extract + community portraits) */}
          <div className="lg:col-span-2 relative">
            <div className="absolute -top-4 right-2 md:right-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg text-right z-30">
              <p className="text-xs uppercase tracking-wider text-[hsl(var(--navy))] font-semibold">
                Where Faith Meets Business
              </p>
              <p className="text-xs text-[hsl(var(--gold))] font-semibold">
                and Lives Are Impacted!
              </p>
            </div>

            {/* Slide stack (cross-fade) */}
            <div
              className="relative aspect-[4/5] md:aspect-[5/6] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-[hsl(var(--gold))]/40 bg-[hsl(var(--navy-dark))]"
              data-testid="hero-slideshow"
            >
              {heroSlides.map((slide, i) => (
                <div
                  key={slide.src}
                  className={`absolute inset-0 transition-opacity duration-[1200ms] ease-in-out ${slide.bgClass || 'bg-[hsl(var(--navy-dark))]'} ${
                    i === slideIdx ? 'opacity-100' : 'opacity-0'
                  }`}
                  data-testid={`hero-slide-${i}`}
                >
                  <img
                    src={slide.src}
                    alt={slide.alt}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    className={`w-full h-full ${slide.label ? 'object-contain p-6' : 'object-cover'}`}
                  />
                  {slide.label && (
                    <div className="absolute bottom-14 left-1/2 -translate-x-1/2 bg-[hsl(var(--navy-dark))]/90 backdrop-blur-sm text-white text-xs md:text-sm font-semibold px-4 py-2 rounded-full shadow-lg border border-[hsl(var(--gold))]/40 whitespace-nowrap max-w-[90%] overflow-hidden text-ellipsis" data-testid={`hero-slide-label-${i}`}>
                      <span className="text-[hsl(var(--gold))]">CK Apparel</span>
                      <span className="mx-2 text-white/40">|</span>
                      <span className="truncate">{slide.label}</span>
                    </div>
                  )}
                </div>
              ))}

              {/* Dots */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20" data-testid="hero-slide-dots">
                {heroSlides.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSlideIdx(i)}
                    aria-label={`Go to slide ${i + 1}`}
                    className={`h-2 rounded-full transition-all ${
                      i === slideIdx
                        ? 'w-6 bg-[hsl(var(--gold))]'
                        : 'w-2 bg-white/60 hover:bg-white/90'
                    }`}
                    data-testid={`hero-slide-dot-${i}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── TRUST BADGES ─────────── */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 -mt-8 relative z-20">
        <div className="bg-[hsl(var(--navy))] rounded-2xl shadow-xl px-6 md:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="trust-badges">
          {TRUST_BADGES.map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.label} className="flex flex-col items-center text-center text-white">
                <div className="w-14 h-14 rounded-full border-2 border-[hsl(var(--gold))] flex items-center justify-center mb-2 bg-[hsl(var(--navy-dark))]/40">
                  <Icon className="h-6 w-6 text-[hsl(var(--gold))]" />
                </div>
                <p className="text-xs md:text-sm font-semibold uppercase tracking-wider">{b.label}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ─────────── VALUES + BIBLE VERSE ─────────── */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-16">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Values box */}
          <div className="bg-white border border-border rounded-2xl p-6 md:p-8" data-testid="values-box">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-12 h-12 rounded-full bg-[hsl(var(--gold))]/15 flex items-center justify-center shrink-0">
                <Users className="h-6 w-6 text-[hsl(var(--gold))]" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg md:text-xl text-[hsl(var(--navy))]">
                  Join a Thriving Community That Values:
                </h3>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 pl-16">
              {CORE_VALUES.map((v) => (
                <div key={v} className="flex items-center gap-2 text-sm md:text-base">
                  <CheckCircle2 className="h-4 w-4 text-[hsl(var(--gold))] shrink-0" />
                  <span className="font-medium">{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bible verse callout */}
          <div className="bg-[hsl(var(--gold))] rounded-2xl p-6 md:p-8 flex flex-col justify-center text-[hsl(var(--navy-dark))]" data-testid="bible-verse">
            <p className="text-lg md:text-xl font-heading font-semibold italic leading-relaxed mb-2">
              &ldquo;Whatever you do, do it all for the glory of God.&rdquo;
            </p>
            <p className="text-sm font-bold tracking-wider uppercase">1 Corinthians 10:31</p>
          </div>
        </div>
      </section>

      {/* ─────────── SHOP. SELL. SHARE. banner ─────────── */}
      <section className="bg-[hsl(var(--navy))] text-white" data-testid="shop-sell-share-banner">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 flex items-center gap-6 flex-wrap">
          <div className="w-16 h-16 rounded-full bg-[hsl(var(--gold))] flex items-center justify-center shrink-0">
            <ShoppingCart className="h-8 w-8 text-[hsl(var(--navy-dark))]" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl md:text-4xl font-heading font-bold tracking-tight">
              SHOP. SELL. SHARE.
            </h2>
            <p className="text-[hsl(var(--gold))] text-lg md:text-xl font-semibold mt-1">
              Make an eternal difference.
            </p>
          </div>
        </div>
      </section>

      {/* ─────────── FOUR AUDIENCE COLUMNS ─────────── */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20" data-testid="audience-columns">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {AUDIENCE_COLUMNS.map((col) => {
            const Icon = col.icon;
            return (
              <div
                key={col.title}
                className="bg-white border border-border rounded-2xl p-6 hover:border-[hsl(var(--gold))]/40 hover:shadow-md transition"
                data-testid={`audience-${col.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-12 h-12 rounded-full bg-[hsl(var(--navy))] flex items-center justify-center mb-4">
                  <Icon className="h-6 w-6 text-[hsl(var(--gold))]" />
                </div>
                <h3 className="font-heading font-bold text-lg text-[hsl(var(--navy))] mb-3 uppercase tracking-wide">
                  {col.title}
                </h3>
                <ul className="space-y-2">
                  {col.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-foreground/80">
                      <span className="text-[hsl(var(--gold))] mt-1 shrink-0">•</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* ─────────── ONE FAITH. MANY NATIONS. ONE KINGDOM. ─────────── */}
      <section className="relative bg-[hsl(var(--navy-dark))] text-white overflow-hidden" data-testid="one-kingdom-band">
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="max-w-md">
            <h2 className="text-4xl md:text-5xl font-heading font-bold leading-tight mb-4">
              ONE FAITH.<br />
              MANY NATIONS.<br />
              <span className="text-[hsl(var(--gold))]">ONE KINGDOM.</span>
            </h2>
            <p className="text-white/80 text-base md:text-lg mb-6">
              Christians from over 100 countries — united by purpose, connected by faith,
              transacting with integrity.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/90 text-[hsl(var(--navy-dark))] font-semibold rounded-full">
                Join Today <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ─────────── EXISTING SECTIONS BELOW — now inherit navy+gold palette ─────────── */}

      {/* Featured Products */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[hsl(var(--gold))] mb-2 block">
                Curated Collection
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[hsl(var(--navy))]">
                Featured Products
              </h2>
            </div>
            <Link to="/products">
              <Button variant="ghost" className="text-primary hover:text-primary/80" data-testid="view-all-products">
                View All <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {['l1','l2','l3','l4','l5','l6','l7','l8'].map((k) => (
                <div key={k} className="animate-pulse">
                  <div className="aspect-square bg-muted rounded-lg mb-4" />
                  <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-4 bg-muted rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product) => (
                <ProductCard key={product.product_id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      <FeaturedVendors />
      <HouseBrandRail />
      <TestimonialsSection />
      <FeaturedStorySection />

      {/* Founding Vendor CTA (now inherits navy + gold) */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[hsl(var(--navy-dark))] via-[hsl(var(--navy))] to-[hsl(var(--navy))] text-white relative overflow-hidden" data-testid="founding-vendor-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[hsl(var(--gold))] rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-[hsl(var(--gold))]/15 border border-[hsl(var(--gold))]/40 px-4 py-2 rounded-full" data-testid="founding-vendor-badge">
                <Sparkles className="h-4 w-4 text-[hsl(var(--gold))]" />
                <span className="text-sm font-bold uppercase tracking-wider text-[hsl(var(--gold))]">
                  Become a Founding Vendor — Free
                </span>
              </div>

              <FoundingSpotsCounter />

              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-heading font-bold leading-tight">
                Start Selling in Minutes
              </h2>

              <p className="text-lg text-white/85 leading-relaxed max-w-xl">
                Sell products or offer services — fashion, real estate, oil & gas, beauty, consulting, weddings, and more. Join as a Founding Vendor and showcase your business to a global audience of believers.
              </p>

              <ul className="space-y-3" data-testid="founding-vendor-benefits">
                {[
                  ['Free early access', 'be first to list before public launch'],
                  ['No setup fees', 'list unlimited products, zero upfront cost'],
                  ['Global reach', 'sell across borders to customers worldwide'],
                ].map(([title, tail]) => (
                  <li key={title} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[hsl(var(--gold))] shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold">{title}</span>
                      <span className="text-white/75"> — {tail}</span>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="flex flex-wrap gap-4 pt-2">
                <Link to="/register">
                  <Button
                    size="lg"
                    className="rounded-full px-8 bg-[hsl(var(--gold))] text-[hsl(var(--navy-dark))] hover:bg-[hsl(var(--gold))]/90 font-semibold shadow-xl"
                    data-testid="cta-become-founding-vendor"
                  >
                    Claim My Founding Spot <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/products">
                  <Button
                    size="lg"
                    variant="outline"
                    className="rounded-full px-8 bg-transparent border-white text-white hover:bg-white hover:text-[hsl(var(--navy))]"
                    data-testid="cta-browse-marketplace"
                  >
                    Browse Marketplace
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative" data-testid="founding-vendor-stats">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-8 shadow-2xl">
                <div className="grid grid-cols-2 gap-6">
                  {[['$0','Setup Fee'],['5min','To Get Live'],['18','Categories Open'],['∞','Product Listings']].map(([k, v]) => (
                    <div key={v} className="text-center">
                      <div className="text-4xl sm:text-5xl font-heading font-medium text-[hsl(var(--gold))]">{k}</div>
                      <div className="text-sm text-white/80 mt-1">{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Install the App */}
      <section className="max-w-5xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <InstallAppSection />
      </section>

      <Footer />
    </div>
  );
}
